/**
 * userDropdownMenu.ts
 * ------------------------------------------------
 * Reactive user dropdown menu builder
 *
 * @author Harvinder Singh
 * @created 2025-09-30
 */

interface MenuItem {
  title: string
  icon?: string
  id?: string
  route?: string
  privilege_group?: string | string[]
  allowed_account?: number[]
  condition?: () => boolean | Promise<boolean>
  disabled?: boolean
  submenu?: MenuItem[]
  [key: string]: any
}

import { computed } from 'vue'
import { useUserStore } from '@/lib/stores'
import router from '@/lib/router/router'

// @ts-ignore
import maropostImg from '@/assets/maropost-insignia.svg'
// @ts-ignore
import ProcessQueueImg from '@/assets/waiting-list-icon.svg'
import { navigateToUrl } from 'single-spa'

export default function useUserDropdownMenu() {
  const userStore = useUserStore()
  const userRoles = userStore.getRoles

  function resolveTo(product: string, url: string) {
    let path = `/${url}`
    if (product === 'neto' || product === 'merchandising') {
      path = `/${product}/${url}`
    } else if (product) {
      path = `/${product}/${userStore.getAccountId}/${url}`
    }

    if (product) {
      navigateToUrl(path)
      return
    }

    // Vue 2 pages leave history.state as `{ key }`. Restore `current` so
    // Vue Router 4 does not replaceState to origin+undefined.
    const state = window.history.state
    const absPath = location.pathname + location.search + location.hash
    if (state?.current !== absPath) {
      window.history.replaceState(
        { ...state, current: absPath },
        '',
      )
    }

    void router.push(path).catch(() => navigateToUrl(path))
  }

  return computed<MenuItem[][]>(() => {
    const sectionOne: MenuItem[] = []
    const sectionTwo: MenuItem[] = []

    /* --------------------------------------------------
     * ACCESS FLAGS (computed once)
     * --------------------------------------------------
     */
    const hasMarketing = userStore.getProductAccess('marketing') && userStore.getProductAccessOnRoles('marketing')
    const hasCommerce = userStore.getProductAccess('commerce') && userStore.getProductAccessOnRoles('commerce')
    const hasService = userStore.getProductAccess('service') && userStore.getProductAccessOnRoles('service')
    const hasRetail = userStore.getProductAccess('retail') && userStore.getProductAccessOnRoles('retail')
    const isTrialExpired = userStore.getIsTrailAccount && userStore.getIsTrialExpired

    const hasAnyProduct = hasMarketing || hasCommerce || hasService || hasRetail
    const hasUserManagementAccess = userRoles.includes('account')

    /* --------------------------------------------------
     * SECTION 1: ACCOUNT / SETTINGS
     * --------------------------------------------------
     */

    if (hasAnyProduct || isTrialExpired) {
      sectionOne.push({
        title: 'Account & Billing',
        icon: 'mdi-account-cog',
        id: 'account-settings-nav-link',
        relative: true,
        onClick: () => resolveTo('accounts', 'account-&-billing'),
      })

      if (hasUserManagementAccess && !isTrialExpired) {
        sectionOne.push({
          title: 'Users & Permissions',
          icon: 'mdi-account',
          id: 'permission-settings-nav-link',
          relative: true,
          onClick: () => resolveTo('accounts', 'users-&-permissions'),
        })
      }

      sectionOne.push({
        title: 'Profile',
        icon: 'mdi-account-circle',
        id: 'profile-settings-nav-link',
        relative: true,
        onClick: () => resolveTo('', 'profile'),
      })
    } else {
      // No product access → Profile only
      sectionOne.push({
        title: 'Profile',
        icon: 'mdi-account-circle',
        id: 'profile-settings-nav-link',
        relative: true,
        onClick: () => resolveTo('', 'profile'),
      })
    }

    if ((hasMarketing || hasCommerce) && !isTrialExpired) {
      sectionOne.push({
        title: 'Connections',
        icon: 'mdi-power-plug',
        id: 'connections-nav-link',
        relative: true,
        onClick: () => resolveTo('accounts', 'connections'),
      })
    }

    if (hasMarketing && !isTrialExpired) {
      sectionOne.push({
        title: 'Process Queue',
        image: ProcessQueueImg,
        id: 'process-queue-nav-link',
        relative: true,
        onClick: () => resolveTo('accounts', 'process-queue'),
      })
    }

    /* --------------------------------------------------
     * SECTION 2: EXTERNAL LINKS
     * --------------------------------------------------
     */

    if ((hasCommerce || hasRetail) && !isTrialExpired) {
      sectionTwo.push({
        title: 'Support',
        icon: 'mdi-headphones',
        id: 'support-nav-link',
        newTab: true,
        path: 'https://portal.maropost.com/support',
      })
    }

    if ((hasMarketing || hasService || hasRetail || isTrialExpired)) {
      sectionTwo.push(
        {
          title: 'Maropost Galaxy',
          image: maropostImg,
          id: 'maropost-galaxy-nav-link',
          newTab: true,
          path: 'https://galaxy.maropost.com/s/',
        },
        {
          title: 'Roadmap',
          icon: 'mdi-emoticon-outline',
          id: 'roadmap-nav-link',
          newTab: true,
          path: 'https://www.maropost.com/coming-soon/',
        },
      )
    }

    if ((hasMarketing || hasCommerce) && !isTrialExpired) {
      sectionTwo.push({
        title: 'System Status',
        icon: 'mdi-speedometer',
        id: 'system-status-nav-link',
        newTab: true,
        path: 'https://status.maropost.com',
      })
    }
    if (hasAnyProduct && !isTrialExpired) {  
      sectionTwo.push({
        title: 'Timezone',
        icon: 'mdi-earth',
        id: 'timezone-nav-link',
        newTab: true,
        onClick: () => resolveTo('', 'profile?tab=preferences'),
      })
    }

    return [sectionOne, sectionTwo]
  })
}
